import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { supabase } from '@/lib/supabase'
import type { Account } from '@limo/database'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useToast } from '@/hooks/use-toast'
import { Loader2, Copy } from 'lucide-react'

const addAccountSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  slug: z
    .string()
    .min(2, 'Slug must be at least 2 characters')
    .regex(/^[a-z0-9-]+$/, 'Slug can only contain lowercase letters, numbers, and hyphens'),
  cloneFromAccount: z.boolean().default(false),
  sourceAccountId: z.string().optional(),
}).refine(
  (data) => !data.cloneFromAccount || data.sourceAccountId,
  { message: 'Please select a source account to clone from', path: ['sourceAccountId'] }
)

type AddAccountFormData = z.infer<typeof addAccountSchema>

interface AddAccountDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function AddAccountDialog({ open, onOpenChange }: AddAccountDialogProps) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [cloneEnabled, setCloneEnabled] = useState(false)

  const form = useForm<AddAccountFormData>({
    resolver: zodResolver(addAccountSchema),
    defaultValues: {
      name: '',
      slug: '',
      cloneFromAccount: false,
      sourceAccountId: undefined,
    },
  })

  // Fetch active accounts for clone dropdown
  const { data: accounts } = useQuery({
    queryKey: ['accounts-select'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('accounts')
        .select('id, name')
        .eq('status', 'active')
        .order('name')
      if (error) throw error
      return data as Pick<Account, 'id' | 'name'>[]
    },
    enabled: cloneEnabled,
  })

  // Auto-generate slug from name
  const watchName = form.watch('name')
  useEffect(() => {
    if (watchName) {
      const slug = watchName
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')
      form.setValue('slug', slug, { shouldValidate: true })
    }
  }, [watchName, form])

  const addMutation = useMutation({
    mutationFn: async (data: AddAccountFormData) => {
      if (data.cloneFromAccount && data.sourceAccountId) {
        // Clone from existing account via edge function
        const { data: { session } } = await supabase.auth.getSession()
        if (!session) throw new Error('Not authenticated')

        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/clone-account`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({
              source_account_id: data.sourceAccountId,
              name: data.name,
              slug: data.slug,
            }),
          }
        )

        if (!response.ok) {
          const error = await response.json()
          throw new Error(error.error || 'Failed to clone account')
        }

        return response.json()
      } else {
        // Standard account creation
        const { data: account, error } = await supabase
          .from('accounts')
          .insert({
            name: data.name,
            slug: data.slug,
            status: 'active',
          })
          .select()
          .single()

        if (error) throw error

        // Create default account settings (website enabled by default)
        await supabase.from('account_settings').insert({
          account_id: account.id,
          key: 'website_enabled',
          value: 'true',
          description: 'Website Enabled',
          is_secret: false,
        })

        return account
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] })

      if (data?.message) {
        // Clone response with details
        toast({
          title: 'Account cloned',
          description: data.message,
        })
      } else {
        toast({
          title: 'Account created',
          description: 'The account has been created with analytics enabled.',
        })
      }

      onOpenChange(false)
      form.reset()
      setCloneEnabled(false)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const onSubmit = (data: AddAccountFormData) => {
    addMutation.mutate(data)
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      form.reset()
      setCloneEnabled(false)
    }
    onOpenChange(open)
  }

  const handleCloneToggle = (checked: boolean) => {
    setCloneEnabled(checked)
    form.setValue('cloneFromAccount', checked)
    if (!checked) {
      form.setValue('sourceAccountId', undefined)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>New Account</DialogTitle>
          <DialogDescription>
            Create a new limo company account.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Acme Limousine" {...field} />
                  </FormControl>
                  <FormDescription>
                    The display name of the limo company
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="slug"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Slug</FormLabel>
                  <FormControl>
                    <Input placeholder="acme-limousine" {...field} />
                  </FormControl>
                  <FormDescription>
                    URL-friendly identifier (auto-generated from name)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Clone from existing account */}
            <div className="rounded-lg border p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Copy className="h-4 w-4 text-muted-foreground" />
                  <div className="space-y-0.5">
                    <label className="text-sm font-medium">Clone from existing account</label>
                    <p className="text-xs text-muted-foreground">
                      Copy all data from another account
                    </p>
                  </div>
                </div>
                <Switch
                  checked={cloneEnabled}
                  onCheckedChange={handleCloneToggle}
                />
              </div>

              {cloneEnabled && (
                <>
                  <FormField
                    control={form.control}
                    name="sourceAccountId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Source Account</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select an account to clone from" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {accounts?.map((account) => (
                              <SelectItem key={account.id} value={account.id}>
                                {account.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <p className="text-xs text-muted-foreground">
                    Settings, vehicles, pricing, pages, FAQs, reviews, and knowledge base will be copied. Integration secrets and transactional data (bookings, analytics) will be skipped.
                  </p>
                </>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={addMutation.isPending}>
                {addMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {cloneEnabled ? 'Clone Account' : 'Create Account'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
